import React, {
  FormEvent,
  KeyboardEvent,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';

import api, { IPresencaByEventoCPF } from 'api/Assessor/presenca';

import LayoutContext from 'components/Layout/LayoutContext';
import Loading from 'components/Modal/__LEGACY__/AbrirVotacaoPautaModal/Loading';
import UnauthorizedScreen from 'pages/Errors/UnauthorizedScreen/UnauthorizedScreen';

import { IAuthReducer } from 'store/modules/AuthReducer';
import { IReducer } from 'store/Reducer';

import {
  clearFormat,
  formatCPFOrCNPJ,
  validarCPF,
} from 'helpers/utils/cpfCnpjMask';

import { languageValues } from 'helpers/utils/languageUtils';
import {
  AssembleiasIcon,
  Button,
  CheckCircleIcon,
  CloseIcon,
  Col,
  Collapse,
  Container,
  IconButton,
  Input,
  Row,
  SearchIcon,
  Table,
  Title,
  ToastTypes,
} from 'plataforma-fundacao-componentes';

import ErrorHandler from 'helpers/utils/ErrorHandler';
import styles from './styles.module.scss';

import pautasApi from 'api/Assessor/pautas';
import AssembleiaPautasNews, {
  IPauta,
} from 'components/__SHARED__/AssembleiaPautas/AssembleiaPautasNews';
import { TipoVotoPauta } from 'helpers/enums/AssembleiaPautaStatus';
import { IAssembleiaAssociadoPautaVoto } from 'store/modules/AssembleiasAssociadoReducer';
import VotacaoFinalizada from './VotacaoFinalizada';

interface PageState {
  lastVote?: string;
}
interface RouteParams {
  idAssembleia: string;
}

export default function VotoPresencial() {
  const [touched, setTouched] = useState<boolean>(false);
  const [currentCpf, setCurrentCPF] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [expand, setExpand] = useState<boolean>(false);
  const [listaPresenca, setListaPresenca] = useState<IPresencaByEventoCPF>();
  const [listaPautas, setListaPautas] = useState<IPauta[]>([]);
  const { idAssembleia } = useParams<RouteParams>();
  const { showToast, setToolbarReadOnly } = useContext(LayoutContext);
  const location = useLocation<PageState | undefined>();
  const auth = useSelector<IReducer, IAuthReducer>((state) => state.auth);

  const votacaoFinalizada = useMemo(() => {
    if (!listaPautas.length || isLoading) return false;
    const pautasCanVote = listaPautas.filter(
      (pauta) =>
        [
          TipoVotoPauta.Vota,
          TipoVotoPauta.VotaNaoColaborador,
          TipoVotoPauta.VotaNaoRestrito,
        ].includes(pauta.tipoVotoPauta) && pauta.itens.length > 0
    );
    if (!pautasCanVote.length) return false;
    const pautasNaoVotadas = pautasCanVote.filter(
      (pauta) => pauta.votos.length === 0
    );

    return pautasNaoVotadas.length === 0;
  }, [listaPautas, isLoading]);

  useEffect(() => {
    if (votacaoFinalizada) {
      setTimeout(() => {
        window.location.reload();
      }, 7000);
    }
  }, [votacaoFinalizada]);

  const getPresencas = useCallback(async () => {
    setIsLoading(true);
    const { fail, data } = await api.findByEventoAndCPF(
      idAssembleia,
      currentCpf.replace(/[^0-9]/g, '')
    );

    if (fail) {
      ErrorHandler(fail);
    }
    if (data) {
      setListaPresenca(data);
    }
    setIsLoading(false);
  }, [currentCpf, idAssembleia]);

  const getPautas = useCallback(async () => {
    setIsLoading(true);
    const { fail, data } = await pautasApi.listarPautasByEventoCPF(
      idAssembleia,
      currentCpf.replace(/[^0-9]/g, '')
    );
    if (fail) {
      ErrorHandler(fail);
    }
    if (data) {
      setListaPautas(
        data.sort((pautaA: any, pautaB: any) => {
          return pautaA.ordem < pautaB.ordem ? -1 : 1;
        })
      );
    }
    setIsLoading(false);
  }, [currentCpf, idAssembleia]);

  const isColaborador = useMemo(() => {
    if (!listaPresenca || isLoading) return false;
    return (
      listaPresenca.listPresencasAgenciaResponse.length === 1 &&
      listaPresenca.listPresencasAgenciaResponse[0].flgColaborador === 1
    );
  }, [isLoading, listaPresenca]);

  const updateVoto = useCallback(
    (idPauta: number, voto: Array<IAssembleiaAssociadoPautaVoto>) => {
      if (!listaPautas.length || isLoading) return;
      const _lista = Array.from(listaPautas);
      const curPauta = _lista.findIndex((pauta) => pauta.idPauta === idPauta);
      if (curPauta === -1) return;
      _lista[curPauta].votos = voto;
      setListaPautas(_lista);
    },
    [listaPautas, isLoading]
  );

  useEffect(() => {
    if (expand) {
      getPresencas();
      getPautas();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expand]);

  const handleCPFChange = useCallback(
    (event: React.ChangeEvent<{ value: unknown }>) => {
      const value = event.target.value as string;
      setCurrentCPF(formatCPFOrCNPJ(value, true));
    },
    []
  );

  const formError = useMemo(() => {
    const cpf = clearFormat(currentCpf);
    if (touched && cpf.length === 0)
      return languageValues.pages.inPersonVoting.error.empty;
    if (cpf.length === 11 && !validarCPF(cpf))
      return languageValues.pages.inPersonVoting.error.invalid;
    if (
      cpf.length === 11 &&
      location.state?.lastVote &&
      cpf === location.state.lastVote
    ) {
      return languageValues.pages.inPersonVoting.error.noRepeat;
    }
    return '';
  }, [currentCpf, touched, location]);

  const handleSubmit = useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (formError.length > 0 || currentCpf.length <= 13) return;
      setIsLoading(true);
      const { fail, data } = await api.findByEventoAndCPF(
        idAssembleia,
        currentCpf.replace(/[^0-9]/g, '')
      );
      if (fail) {
        showToast({
          pauseOnFocusLoss: true,
          theme: ToastTypes.Error,
          showStatusBar: true,
          timeout: 4000,
          label: languageValues.toasts.error.keyErrors(fail!?.message),
        });
        setIsLoading(false);
      }
      if (data) {
        setIsLoading(false);
        if (
          data &&
          data.listPresencasAgenciaResponse &&
          data.listPresencasAgenciaResponse.length
        ) {
          setExpand(true);
        } else {
          showToast({
            pauseOnFocusLoss: true,
            theme: ToastTypes.Error,
            showStatusBar: true,
            timeout: 4000,
            label: languageValues.toasts.error.presencaNotFound,
          });
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentCpf, idAssembleia, formError]
  );

  useEffect(() => {
    setToolbarReadOnly(true);
  }, [setToolbarReadOnly]);

  if (
    !auth.usuario?.perfil.assessorRoute.assembleias.pautas.incluirVotoManual
  ) {
    return <UnauthorizedScreen />;
  }

  return isLoading ? (
    <Loading />
  ) : (
    <Container className={styles.root} verticalPadding>
      <Row>
        <Collapse opened={!votacaoFinalizada && !expand} animateOpacity>
          <Row>
            <Col>
              <Title>{languageValues.pages.inPersonVoting.title}</Title>
            </Col>
            <Col>
              <p>{languageValues.pages.inPersonVoting.subTitle}</p>
            </Col>
          </Row>
          <Col cols={[6, 6, 6, 6, 6]} className={styles.input_container}>
            <Input
              data-testid={'input-cpf'}
              value={currentCpf}
              onChange={handleCPFChange}
              label={
                languageValues.pages.inPersonVoting.components.inputCPFLabel
              }
              placeholder={
                languageValues.pages.inPersonVoting.components
                  .inputCPFPlaceholder
              }
              rightObject={
                <Col noPadding gap={8}>
                  {currentCpf.length > 1 ? (
                    <IconButton
                      icon={<CloseIcon />}
                      onClick={() => setCurrentCPF('')}
                    />
                  ) : undefined}
                  <IconButton
                    icon={<SearchIcon />}
                    onClick={(e: any) =>
                      !formError || !currentCpf ? handleSubmit(e) : undefined
                    }
                  />
                </Col>
              }
              error={formError.length > 0}
              helperText={formError.length > 0 ? formError : undefined}
              onBlur={() => setTouched(true)}
              onKeyDown={(evt: KeyboardEvent<HTMLInputElement>) => {
                if (evt.key === 'Enter') {
                  return !formError || !currentCpf
                    ? handleSubmit(evt as unknown as FormEvent<HTMLFormElement>)
                    : undefined;
                }
              }}
            />
          </Col>
        </Collapse>
      </Row>
      <Row>
        <Collapse opened={!votacaoFinalizada && expand} animateOpacity>
          <Row>
            <Col>
              <Title>
                {languageValues.pages.inPersonVoting.effectuationOfVote.title}
              </Title>
            </Col>
            <Col>
              <p>
                {languageValues.pages.inPersonVoting.effectuationOfVote.subTitle(
                  listaPresenca!?.nomeRepresentante!
                )}
              </p>
            </Col>
          </Row>
          <Row>
            <Col>
              <Table
                columns={[
                  {
                    key: 'account',
                    value: 'Conta',
                    props: {
                      align: 'center',
                    },
                  },
                  {
                    key: 'name',
                    value: 'Nome do Associado',
                    props: {
                      align: 'center',
                    },
                  },
                  {
                    key: 'presence',
                    value: 'Presença',
                    props: {
                      align: 'center',
                    },
                  },
                ]}
                lines={listaPresenca!?.listPresencasAgenciaResponse.map(
                  (x: any) => {
                    return {
                      account: x.numeroConta,
                      name: x.nomeAssociado,
                      presence: (
                        <div>
                          <div className={styles.presence_column}>
                            <p>Confirmada na agência</p> <CheckCircleIcon />
                          </div>
                        </div>
                      ),
                    };
                  }
                )}
              />
            </Col>
          </Row>
          <Row>
            {isColaborador && (
              <Col>
                <p>{languageValues.pages.inPersonVoting.collaboratorPerson}</p>
                <div>
                  <Button
                    leftIcon={<AssembleiasIcon />}
                    onClick={() => setExpand(false)}
                  >
                    {
                      languageValues.pages.inPersonVoting.components
                        .confirmLabel
                    }
                  </Button>
                </div>
              </Col>
            )}
            {!isColaborador && (
              <Col className={styles.staves_content}>
                <AssembleiaPautasNews
                  pautas={listaPautas as any}
                  cpfEfetivacaoPresencial={currentCpf.replace(/[^0-9]/g, '')}
                  updateVotoPresencial={updateVoto}
                  idEvento={Number(idAssembleia)}
                  isAssessor={true}
                />
              </Col>
            )}
          </Row>
        </Collapse>
      </Row>
      <Row>
        <Collapse opened={Boolean(votacaoFinalizada)}>
          <VotacaoFinalizada
            cpf={currentCpf.replace(/[^0-9]/g, '')}
            idAssembleia={idAssembleia}
          />
        </Collapse>
      </Row>
    </Container>
  );
}
